import React from "react";
import { HelpCircle, Check } from "lucide-react";
import { formatINR } from "@/lib/api";
import useConfigOptions from "@/lib/useConfigOptions";

// Renders one configurator option group (e.g. "Upper Body Shape", "Cuff").
// Admin photos (image + label + optional price) are used when present,
// otherwise the built-in line-icon set. Options lay out in a responsive grid.
export default function ConfigOptionGroup({
  group, fallbackOptions, FallbackIcon, selected, onChange, testidPrefix,
  compact = false, showPrice = false, cols = "", maxWidth = "",
}) {
  const adminOptions = useConfigOptions(group);
  const options = adminOptions.length > 0 ? adminOptions : (fallbackOptions || []);
  const gridCols = cols || "grid-cols-2 sm:grid-cols-3 md:grid-cols-4";

  return (
    <div className={`grid ${gridCols} gap-3 ${maxWidth}`}>
      {options.map((o) => {
        const on = selected === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            data-testid={`${testidPrefix}-${o.key}`}
            title={o.help}
            className={`group relative text-center rounded-xl border-2 p-3 flex flex-col items-center gap-2 transition-all duration-200 ${
              on ? "border-squire-jeans bg-squire-jeans/[0.04] shadow-sm" : "border-black/10 hover:border-black/30 hover:shadow-sm"
            }`}
          >
            {on && <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-squire-jeans text-white grid place-items-center"><Check size={12} /></span>}
            {o.image ? (
              <div className={`w-full ${compact ? "h-16" : "h-24"} overflow-hidden rounded-lg bg-[#F7F7F5] flex items-center justify-center`}>
                <img src={o.image} alt={o.label} className="w-full h-full object-contain" />
              </div>
            ) : FallbackIcon ? (
              <div className={`w-full ${compact ? "h-12" : "h-16"} flex items-center justify-center ${on ? "text-squire-jeans" : "text-black/60"}`}>
                <FallbackIcon variant={o.key} />
              </div>
            ) : (
              <div className={`w-full ${compact ? "h-10" : "h-12"} rounded-lg bg-[#F7F7F5] grid place-items-center text-black/30 text-xs`}>{(o.label || "?").slice(0, 2).toUpperCase()}</div>
            )}
            <div className="text-xs md:text-sm leading-snug font-medium">{o.label}</div>
            {showPrice && (
              <div className={`text-[11px] ${o.price > 0 ? "text-squire-jeans" : "text-[#9CA3AF]"}`}>{o.price > 0 ? `+${formatINR(o.price)}` : "Included"}</div>
            )}
            {o.help && <HelpCircle size={12} className="absolute top-2 left-2 text-black/25" />}
          </button>
        );
      })}
    </div>
  );
}
